import { useState, useEffect } from 'react';
import { Box, Typography, Paper, useTheme, Link } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import PrimaryButton from '../../../components/styled/PrimaryButton';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useLoanContext } from '../LoanContext';
import FieldRow from '../../../components/FieldRow';
import { usePayoffValidation } from '../../../hooks/useValidation';
import { BRAND_TABLE_TEXT_COLOR } from '../../../styles/tableColors';
import { formatDateForAPI } from '../../../utils/dateUtils';

// Simple formatting function for currency and percentage
const formatValue = (key: string, value: string | number | null | undefined): string => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }

  // Currency fields
  const currencyFields = [
    'principalOutstanding', 'interestAmount', 'totalPayoffAmount', 
    'otherFees', 'prepaymentPenalty', 'exitFees'
  ];

  if (currencyFields.includes(key)) {
    const numValue = parseFloat(String(value));
    if (!isNaN(numValue)) {
      return `£${numValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }

  // Percentage fields
  if (key === 'interestRate') {
    const numValue = parseFloat(String(value));
    if (!isNaN(numValue)) {
      return `${numValue.toFixed(2)}%`;
    }
  }

  // Date fields
  if (key === 'payoffDate' || key === 'maturityDate') {
    if (typeof value === 'string' && value.includes('-')) {
      const date = new Date(value);
      return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
    }
    return String(value);
  }

  return String(value);
};

type PayoffData = {
  payoffDate: string;
  interestRate: number;
  maturityDate: string;
  principalOutstanding: number;
  interestAmount: number;
  totalPayoffAmount: number;
  otherFees?: number;
  prepaymentPenalty?: number;
  exitFees?: number;
};

const ALLOWED_PRODUCT_NAMES_FOR_EXIT_FEES = [
  'Property Bridging Loans- Interest Only (Fixed Rate)',
  'Property Bridging Loans- Interest Only (Variable Rate)',
  'Property Bridging Loans- Rolled Up Interest (Fixed Rate)',
  'Property Bridging Loans- Rolled Up Interest (Variable Rate)'
];

const PayoffEstimatorTab = () => {
  const theme = useTheme();
  const { loan } = useLoanContext();
  const [payoffDate, setPayoffDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [payoffData, setPayoffData] = useState<PayoffData | null>(null);
  const [error, setError] = useState('');
  const [showPayoffDetails, setShowPayoffDetails] = useState(false);
  const { validatePayoffForm, clearErrors } = usePayoffValidation();
  const [payoffDateError, setPayoffDateError] = useState<string | null>(null);
  // Key to force DatePicker re-render and reset its internal state
  const [pickerKey, setPickerKey] = useState(0);
  
  // Check if Exit Fees should be displayed based on loan product name
  const productNameForFees = loan?.productName || loan?.loanProductName;
  const shouldShowExitFees = productNameForFees && 
    ALLOWED_PRODUCT_NAMES_FOR_EXIT_FEES.includes(productNameForFees);
  const fallbackToday = new Date();
  fallbackToday.setHours(0, 0, 0, 0);
  const minPayoffDate = loan?.currentSystemDate ? new Date(loan.currentSystemDate) : fallbackToday;
  const maxPayoffDate = loan?.maxPayoffDate ? new Date(loan.maxPayoffDate) : undefined;

  useEffect(() => {
    if (!loan) return;
    if (!payoffDate) {
      const prefillDate = loan.currentSystemDate
        ? new Date(loan.currentSystemDate)
        : new Date();
      if (!isNaN(prefillDate.getTime())) {
        const formattedPrefillDate = formatDateForAPI(prefillDate);
        if (formattedPrefillDate) {
          setPayoffDate(formattedPrefillDate);
        }
      }
    }
  }, [loan, payoffDate]);

  // Debug: Check what loan data we're receiving

  // Sync validation errors with payoffDate and loan
  useEffect(() => {
    if (!loan) return;
    const validationErrors = validatePayoffForm(payoffDate, loan || undefined);
    if (Object.keys(validationErrors).length === 0) {
      clearErrors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoffDate, loan]);

  // Only handle final date selection - not intermediate changes
  const handleDateChange = (date: Date | null) => {
    if (!date) {
      setPayoffDate('');
      setPayoffDateError(null);
      return;
    }
    
    if (!isNaN(date.getTime())) {
      const newDateString = formatDateForAPI(date);
      if (!newDateString) return;
      
      setPayoffDate(newDateString);
      
      if (!loan) return;
      // Validate the selected date
      const validationErrors = validatePayoffForm(newDateString, loan || undefined);
      setPayoffDateError(validationErrors.transactionDate || null);
      setError(''); // Clear generic error
    }
  };

  // Clear the picker and reset its internal state
  const clearDate = () => {
    setPayoffDate('');
    setPayoffDateError(null);
    setPickerKey(prev => prev + 1); // Force picker re-render to clear its internal state
  };

  const handleCalculate = async () => {
    if (!payoffDate || !loan) return;
    
    
    // Validate the payoff date
    const validationErrors = validatePayoffForm(payoffDate, loan || undefined);
    setPayoffDateError(validationErrors.transactionDate || null);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    
    setLoading(true);
    setError('');
    setShowPayoffDetails(false);
    clearErrors();
    
    try {
      const apiUrl = `/api/loans/${loan.id}/payoff-estimator`;
      const params = {
        transactionDate: formatDateForAPI(new Date()),
        payOffDate: payoffDate,
        payoffQuoteName: 'React Estimator',
        generateQuoteForEntireFacility: false,
      };
      
      
      const response = await axios.get(apiUrl, {
        params,
        withCredentials: true
      });
      
      
      if (response.data.success) {
        setPayoffData(response.data.data);
        setShowPayoffDetails(true);
      } else {
        setError(response.data.message || 'Failed to fetch payoff data');
      }
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Could not fetch payoff details.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    const loanId = loan?.id;
    if (!loanId) {
      setError('Loan Account Id is required to download PDF.');
      return;
    }

    try {
      setError('');
      const accountNumber = loan?.name || loanId || 'Unknown';
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      const todayDate = `${day}/${month}/${year}`;
      const baseFileName = `${accountNumber} - Payoff Quote - ${todayDate}`;
      const endpoint = `/api/loans/${encodeURIComponent(loanId)}/payoffpdfrestapi`;

      const response = await axios.get(endpoint, {
        responseType: 'blob',
        withCredentials: true,
        validateStatus: () => true,
      });
      const contentType = String(response.headers?.['content-type'] || '');

      if (response.status >= 400 || !contentType.toLowerCase().includes('application/pdf')) {
        throw new Error('Expected PDF response from middleware endpoint.');
      }

      saveAs(response.data, `${baseFileName}.pdf`);
    } catch {
      setError('Failed to generate PDF file. Please try again or contact support.');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Info Banner */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          background: theme.palette.mode === 'dark' ? '#232228' : '#f5f5f5',
          p: 3,
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40 }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16v12H4V6zm1 1v10h14V7H5zm2 2h10v2H7V9zm0 3h10v2H7v-2z" fill="#98773b"/>
          </svg>
        </Box>
        <Typography sx={{ fontSize: '1.1em', color: BRAND_TABLE_TEXT_COLOR }}>
          Calculate your loan payoff amount for any date up to your next payment due date.
        </Typography>
      </Box>
      {/* Calculator Form */}
      {!showPayoffDetails && (
        <Paper
          sx={{
            background: theme.palette.mode === 'dark' ? '#1A1A1A' : '#fff',
            borderRadius: 3,
            p: 4,
            mb: 4,
            width: '100%',
            color: theme.palette.mode === 'dark' ? '#fff' : '#232228',
            boxShadow: theme.palette.mode === 'dark' ? undefined : '0 2px 16px #0001',
            border: theme.palette.mode === 'dark' ? undefined : '1.5px solid #eee',
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, color: BRAND_TABLE_TEXT_COLOR, textAlign: 'left', fontFamily: "'Libre Franklin', sans-serif", fontWeight: 600 }}>Enter details</Typography>
          <Box mb={3}>
            <Typography sx={{ mb: 1.5, color: BRAND_TABLE_TEXT_COLOR, textAlign: 'left', fontFamily: "'Libre Franklin', sans-serif", fontWeight: 500 }}>
              Select a Payoff Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                key={pickerKey} // Forces complete re-render when changed
                value={payoffDate ? new Date(payoffDate) : null}
                onChange={handleDateChange}
                format="dd/MM/yyyy"
                minDate={minPayoffDate}
                maxDate={maxPayoffDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    variant: 'outlined',
                    label: '',
                    sx: {
                      '& .MuiInputBase-input': {
                        color: `${BRAND_TABLE_TEXT_COLOR} !important`,
                        background: theme.palette.mode === 'dark' ? '#232228' : '#fff',
                        fontFamily: "'Libre Franklin', sans-serif",
                        fontSize: '1em',
                        '::placeholder': {
                          color: BRAND_TABLE_TEXT_COLOR,
                          opacity: 1,
                        },
                      },
                      'input': {
                        color: `${BRAND_TABLE_TEXT_COLOR} !important`,
                        background: theme.palette.mode === 'dark' ? '#232228' : '#fff',
                        fontFamily: "'Libre Franklin', sans-serif",
                        fontSize: '1em',
                        '::placeholder': {
                          color: BRAND_TABLE_TEXT_COLOR,
                          opacity: 1,
                        },
                      },
                      "input[type='text']": {
                        color: `${BRAND_TABLE_TEXT_COLOR} !important`,
                      },
                      background: theme.palette.mode === 'dark' ? '#232228' : '#fff',
                      borderRadius: 1,
                      '& .MuiInputLabel-root': {
                        color: theme.palette.mode === 'dark' ? '#fff' : '#232228',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.mode === 'dark' ? '#444' : '#ccc',
                      },
                      '& .MuiSvgIcon-root': {
                        color: BRAND_TABLE_TEXT_COLOR,
                      },
                    },
                    InputLabelProps: { shrink: true },
                    placeholder: 'dd/mm/yyyy',
                  }
                }}
              />
            </LocalizationProvider>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Box />
              {payoffDate && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: theme.palette.primary.main, 
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onClick={clearDate}
                >
                  Clear Date
                </Typography>
              )}
            </Box>
            {payoffDate && (
              <Typography variant="caption" sx={{ color: theme.palette.mode === 'dark' ? '#bdbdbd' : '#666', display: 'block', mt: 0.5 }}>
                Selected: {new Date(payoffDate).toLocaleDateString('en-GB')}
              </Typography>
            )}
            {payoffDateError && (
              <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                {payoffDateError}
              </Typography>
            )}
          </Box>
          <PrimaryButton style={{ width: '100%' }} onClick={handleCalculate} disabled={loading || !!payoffDateError || !payoffDate}>
            {loading ? 'Calculating...' : 'Calculate'}
          </PrimaryButton>
          {error && !payoffDateError && (
            <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
          )}
        </Paper>
      )}
      {/* Estimated Payoff Details */}
      {showPayoffDetails && payoffData && (
        <Box>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2, textAlign: 'left', fontFamily: "'Libre Franklin', sans-serif", fontWeight: 600 }}>Estimated Payoff Details</Typography>
          <Paper
            sx={{
              background: theme.palette.mode === 'dark' ? '#1A1A1A' : '#fff',
              borderRadius: 3,
              p: 2.5,
              color: theme.palette.mode === 'dark' ? '#fff' : '#232228',
              width: '100%',
              boxShadow: theme.palette.mode === 'dark' ? undefined : '0 2px 16px #0001',
              border: theme.palette.mode === 'dark' ? undefined : '1.5px solid #eee',
            }}
          >
            <FieldRow 
              fieldKey="transactionDate" 
              value={formatValue("payoffDate", payoffData.payoffDate)}
            />
            <FieldRow 
              fieldKey="interestRate" 
              value={formatValue("interestRate", payoffData.interestRate)}
            />
            <FieldRow 
              fieldKey="maturityDate" 
              value={formatValue("maturityDate", payoffData.maturityDate)}
            />
            <FieldRow 
              fieldKey="principalBalance" 
              value={formatValue("principalOutstanding", payoffData.principalOutstanding)}
            />
            <FieldRow 
              fieldKey="accruedInterest" 
              value={formatValue("interestAmount", payoffData.interestAmount)}
            />
            <FieldRow 
              fieldKey="totalPayoffAmount"
              value={formatValue("totalPayoffAmount", payoffData.totalPayoffAmount)}
            />
            <FieldRow 
              fieldKey="otherFees" 
              value={formatValue("otherFees", payoffData.otherFees)}
            />
            <FieldRow 
              fieldKey="prepaymentPenalty" 
              value={formatValue("prepaymentPenalty", payoffData.prepaymentPenalty)}
            />
            {shouldShowExitFees && (
              <FieldRow 
                fieldKey="exitFees" 
                value={formatValue("exitFees", payoffData.exitFees)}
              />
            )}
          </Paper>
          <Typography
            variant="body2"
            sx={{
              mt: 3,
              color: theme.palette.mode === 'dark' ? '#bdbdbd' : '#666',
              fontSize: '14px',
              fontFamily: "'Libre Franklin', sans-serif",
              fontStyle: 'italic',
              textAlign: 'left',
              lineHeight: 1.6
            }}
          >
            This is an estimate of the Payoff amount for the provided date. To apply for a formal payoff quotation,{' '}
            <Link
              href="mailto:accounts@momentafinance.co.uk"
              sx={{
                color: 'inherit',
                textDecoration: 'underline',
                fontStyle: 'italic',
                '&:hover': { color: theme.palette.primary?.main ?? '#b39ddb' }
              }}
            >
              please contact Momenta Finance Support team
            </Link>
            .
          </Typography>
          <PrimaryButton
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              width: '100%',
            }}
          >
            Download
          </PrimaryButton>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      )}
      {loading && <Typography sx={{ color:'#fff', fontFamily: "'Libre Franklin', sans-serif"}}>Loading payoff details...</Typography>}
    </Box>
  );
};

export default PayoffEstimatorTab;
